import { useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  useBillableSummary,
  useDownloadBillableSummaryCsv,
  useWorkspace,
  type ReportRangeArgs,
} from '@fluxter/shared';
import { Download, LayoutGrid, Rows3, BarChart3 } from 'lucide-react';
import { useSetTopBarSlots } from '@/components/TopBarSlots';
import { SkeletonList, SkeletonStatCards } from '@/components/Skeletons';
import { ReportsKpiCard, type KpiDeltaDirection } from '@/components/ReportsKpiCard';
import {
  ReportsRangePicker,
  rangeForPreset,
} from '@/components/ReportsRangePicker';
import { ReportsSection } from '@/components/ReportsSection';
import {
  ReportsProjectHeader,
  ReportsProjectRow,
  type BillingType,
} from '@/components/ReportsProjectRow';
import {
  ReportsUserHeader,
  ReportsUserRow,
} from '@/components/ReportsUserRow';
import { StackBar, type StackBarSegment } from '@/components/charts/StackBar';
import { useLocalPref } from '@/lib/use-local-pref';
import { projectAccent } from '@/lib/deterministic-color';
import { cn } from '@/lib/utils';

/* ─ API shape adapters ───────────────────────────────────────────── */

interface ProjectRow {
  project_id?: string;
  project_slug?: string;
  name?: string;
  billing_type?: BillingType;
  flat_fee_cents?: number | null;
  minutes?: number;
  billable_minutes?: number;
  amount_cents?: number;
  daily_minutes?: number[];
}

interface UserRow {
  user_id?: string;
  name?: string;
  email?: string;
  minutes?: number;
  billable_minutes?: number;
  amount_cents?: number;
}

interface SummaryTotals {
  minutes: number;
  billable_minutes: number;
  amount_cents: number;
}

interface SummaryData {
  data?: {
    currency?: string;
    from?: string | null;
    to?: string | null;
    day_count?: number;
    totals?: SummaryTotals;
    previous_totals?: SummaryTotals | null;
    by_project?: ProjectRow[];
    by_user?: UserRow[];
  };
}

/* ─ Helpers ──────────────────────────────────────────────────────── */

const fmtMoney = (cents: number | undefined | null, currency: string | undefined): string => {
  if (cents === undefined || cents === null) return '—';
  if (!currency) return (cents / 100).toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    return (cents / 100).toFixed(2);
  }
};

const fmtHours = (minutes: number): string => {
  if (!minutes || minutes <= 0) return '0h';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

/**
 * Compute a delta-pill spec from current vs previous values.
 *  - prev = 0 + curr > 0 → `new` badge
 *  - prev = 0 + curr = 0 → no pill (returns undefined)
 *  - else → up/down/flat with formatted % string
 */
const deriveDelta = (
  curr: number,
  prev: number | null | undefined,
): { delta: string; deltaDir: KpiDeltaDirection } | undefined => {
  if (prev === null || prev === undefined) return undefined;
  if (prev === 0 && curr === 0) return undefined;
  if (prev === 0 && curr > 0) return { delta: 'new', deltaDir: 'new' };
  const diff = curr - prev;
  const pct = (diff / prev) * 100;
  const fmt = `${Math.abs(pct).toFixed(1)}%`;
  if (Math.abs(pct) < 0.1) return { delta: '0%', deltaDir: 'flat' };
  return { delta: fmt, deltaDir: pct > 0 ? 'up' : 'down' };
};

/**
 * Compute the local-tz weekend day indexes inside [from, to). The
 * `Sparkbars` component dims these so weekend bars read as ambient
 * activity rather than primary work.
 */
const weekendIndexesIn = (range: ReportRangeArgs): number[] => {
  if (!range.from || !range.to) return [];
  const a = new Date(range.from);
  const b = new Date(range.to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return [];
  const out: number[] = [];
  const days = Math.max(0, Math.round((b.getTime() - a.getTime()) / 86_400_000));
  for (let i = 0; i < days; i++) {
    const d = new Date(a.getTime() + i * 86_400_000);
    const dow = d.getUTCDay(); // anchored to UTC days, matches backend
    if (dow === 0 || dow === 6) out.push(i);
  }
  return out;
};

const isLayout = (raw: unknown): raw is 'stacked' | 'split' =>
  raw === 'stacked' || raw === 'split';

const defaultRange = (): ReportRangeArgs => rangeForPreset('month');

/* ─ Page ─────────────────────────────────────────────────────────── */

export const WorkspaceReportsPage = () => {
  const { workspaceSlug } = useParams({ from: '/w/$workspaceSlug/reports' });
  const workspace = useWorkspace(workspaceSlug);
  const [range, setRange] = useState<ReportRangeArgs>(() => defaultRange());

  const summary = useBillableSummary(workspaceSlug, range);
  const download = useDownloadBillableSummaryCsv(workspaceSlug);

  const [layout, setLayout] = useLocalPref<'stacked' | 'split'>(
    `reports-layout:${workspaceSlug}`,
    'stacked',
    isLayout,
  );

  const workspaceName = (workspace.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;

  const data = (summary.data as SummaryData | undefined)?.data;
  const totals = data?.totals;
  const previousTotals = data?.previous_totals ?? null;
  const currency = data?.currency;
  const byProject = useMemo(() => data?.by_project ?? [], [data]);
  const byUser = useMemo(() => data?.by_user ?? [], [data]);

  // Workspace-wide grand-billable = time-based amount + flat fees
  // for flat projects with activity. Mirrors the CSV's total_amount.
  const grandTotalCents = useMemo(() => {
    let total = totals?.amount_cents ?? 0;
    for (const row of byProject) {
      if (row.billing_type === 'flat' && (row.minutes ?? 0) > 0) {
        total += row.flat_fee_cents ?? 0;
      }
    }
    return total;
  }, [totals, byProject]);

  // KPI sparklines: sum across all projects per day.
  const dailyHours = useMemo(() => {
    const dayCount = data?.day_count ?? 0;
    if (dayCount === 0) return [];
    const out = new Array(dayCount).fill(0) as number[];
    for (const p of byProject) {
      const dm = p.daily_minutes ?? [];
      for (let i = 0; i < dayCount && i < dm.length; i++) {
        out[i] = (out[i] ?? 0) + (dm[i] ?? 0);
      }
    }
    return out;
  }, [byProject, data?.day_count]);

  const dailyBillable = useMemo(() => {
    const dayCount = data?.day_count ?? 0;
    if (dayCount === 0) return [];
    const out = new Array(dayCount).fill(0) as number[];
    for (const p of byProject) {
      if (p.billing_type === 'none') continue;
      const dm = p.daily_minutes ?? [];
      for (let i = 0; i < dayCount && i < dm.length; i++) {
        out[i] = (out[i] ?? 0) + (dm[i] ?? 0);
      }
    }
    return out;
  }, [byProject, data?.day_count]);

  const weekendIndices = useMemo(() => weekendIndexesIn(range), [range]);

  // Stack-bar segments for the project-mix visualization. Each
  // project gets its hash-derived hue.
  const stackSegments = useMemo<StackBarSegment[]>(
    () =>
      byProject.map((p) => ({
        id: p.project_id ?? p.project_slug ?? p.name ?? 'unknown',
        label: p.name ?? '—',
        color: projectAccent(p.project_slug ?? null),
        minutes: p.minutes ?? 0,
      })),
    [byProject],
  );

  const totalFlatCents = useMemo(
    () =>
      byProject.reduce(
        (sum, p) =>
          p.billing_type === 'flat' && (p.minutes ?? 0) > 0
            ? sum + (p.flat_fee_cents ?? 0)
            : sum,
        0,
      ),
    [byProject],
  );

  const avgBillablePct = useMemo(() => {
    if (byUser.length === 0) return 0;
    let sum = 0;
    let count = 0;
    for (const u of byUser) {
      const t = u.minutes ?? 0;
      if (t > 0) {
        sum += ((u.billable_minutes ?? 0) / t) * 100;
        count++;
      }
    }
    return count === 0 ? 0 : Math.round(sum / count);
  }, [byUser]);

  /* ─ Topbar slots ─────────────────────────────────────────────── */
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const handleDownload = async () => {
    setDownloadError(null);
    try {
      await download.mutateAsync(range);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Could not start download.');
    }
  };

  const topBarLeft = useMemo(
    () => (
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-1.5 text-[13px]"
      >
        <Link
          to="/w/$workspaceSlug"
          params={{ workspaceSlug }}
          className="text-foreground-muted hover:text-foreground transition-colors"
        >
          {workspaceName ?? 'Workspace'}
        </Link>
        <span aria-hidden="true" className="text-foreground-faint">
          /
        </span>
        <span className="text-foreground font-medium" aria-current="page">
          Reports
        </span>
      </nav>
    ),
    [workspaceSlug, workspaceName],
  );

  const topBarCta = useMemo(
    () => (
      <button
        type="button"
        onClick={() => void handleDownload()}
        disabled={download.isPending}
        className={cn(
          'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-lg border-0 px-2.5 text-[12.5px] font-medium text-white transition-shadow duration-100',
          'bg-[var(--primary)]',
          'shadow-[0_2px_8px_oklch(0.3_0.06_245/0.3)] hover:shadow-[0_3px_10px_oklch(0.3_0.06_245/0.4)]',
          'dark:shadow-[0_2px_12px_oklch(0.55_0.12_245/0.4)]',
          'disabled:opacity-50',
        )}
      >
        <Download className="size-3.5" />
        {download.isPending ? 'Preparing…' : 'Download CSV'}
      </button>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [download.isPending],
  );

  useSetTopBarSlots({ left: topBarLeft, cta: topBarCta });

  /* ─ Render ───────────────────────────────────────────────────── */

  const hourDeltas = deriveDelta(totals?.minutes ?? 0, previousTotals?.minutes ?? null);
  const billableDeltas = deriveDelta(totals?.billable_minutes ?? 0, previousTotals?.billable_minutes ?? null);
  const timeDeltas = deriveDelta(totals?.amount_cents ?? 0, previousTotals?.amount_cents ?? null);
  // Grand total has no direct prior comparison from the API (it
  // includes flat fees which the prior-period query doesn't compute
  // separately). Approximate: use the prior amount_cents delta as a
  // proxy. Fine for v1; revisit if the team wants exact prior grand.
  const grandDeltas = timeDeltas;

  return (
    <div className="relative flex flex-col gap-4">
      {/* Ambient glow — same vocabulary as Notes / Calendar / Board. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 blur-[20px]"
        style={{
          background:
            'radial-gradient(50% 40% at 90% 0%, var(--brand-glow-1), transparent 65%),' +
            'radial-gradient(40% 50% at 5% 20%, var(--brand-glow-2), transparent 60%)',
        }}
      />

      <header className="relative z-[1]">
        <div
          className={cn(
            'text-foreground-faint mb-1.5 inline-flex items-center gap-2 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em]',
            "before:h-px before:w-[18px] before:bg-[var(--brand-accent)] before:opacity-70 before:content-['']",
          )}
        >
          Workspace · Billable summary
        </div>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-foreground text-[26px] font-semibold tracking-[-0.025em]">
              Reports
            </h1>
            <p className="text-foreground-faint mt-1 max-w-[720px] text-[12.5px] leading-[1.5]">
              Cross-project totals. Hourly amounts come from logged time × rate snapshot;
              flat-fee projects contribute their contracted fee when they had activity in the range.
            </p>
          </div>
          {/* Layout switcher — stacked vs split (Q16). Persisted via
              useLocalPref keyed by workspace. */}
          <div
            role="group"
            aria-label="Layout"
            className={cn(
              'inline-flex items-center gap-0.5 rounded-[9px] border p-[3px]',
              'border-border bg-secondary',
            )}
          >
            {(
              [
                { id: 'stacked' as const, label: 'Stacked', Icon: Rows3 },
                { id: 'split' as const, label: 'Split', Icon: LayoutGrid },
              ]
            ).map(({ id, label, Icon }) => {
              const active = id === layout;
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={active}
                  aria-label={label}
                  onClick={() => setLayout(id)}
                  className={cn(
                    'inline-flex size-6 items-center justify-center rounded-md border-0',
                    active
                      ? 'bg-background text-foreground shadow-[0_1px_2px_oklch(0_0_0/0.06)]'
                      : 'text-foreground-muted hover:text-foreground bg-transparent',
                  )}
                >
                  <Icon className="size-3.5" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {downloadError ? (
        <p className="text-destructive relative z-[2] text-sm" role="alert">
          {downloadError}
        </p>
      ) : null}

      <div className="relative z-[2]">
        <ReportsRangePicker range={range} onChange={setRange} />
      </div>

      {summary.isLoading ? (
        <div className="relative z-[1] space-y-4">
          <SkeletonStatCards count={4} />
          <SkeletonList rows={5} />
          <SkeletonList rows={5} />
        </div>
      ) : (
        <>
          {/* KPI strip ─────────────────────────────────────────── */}
          <div className="relative z-[1] grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <ReportsKpiCard
              railIndex={1}
              label="Hours logged"
              value={Math.floor((totals?.minutes ?? 0) / 60)}
              unit="h"
              sub={`${totals?.minutes ?? 0} min total`}
              delta={hourDeltas?.delta}
              deltaDir={hourDeltas?.deltaDir}
              sparkValues={dailyHours}
              sparkColor="oklch(0.65 0.13 18)"
            />
            <ReportsKpiCard
              railIndex={2}
              label="Billable"
              value={Math.floor((totals?.billable_minutes ?? 0) / 60)}
              unit="h"
              sub={
                (totals?.minutes ?? 0) > 0
                  ? `${Math.round(
                      ((totals?.billable_minutes ?? 0) /
                        (totals?.minutes ?? 1)) *
                        100,
                    )}% of total`
                  : '—'
              }
              delta={billableDeltas?.delta}
              deltaDir={billableDeltas?.deltaDir}
              sparkValues={dailyBillable}
              sparkColor="oklch(0.65 0.13 245)"
            />
            <ReportsKpiCard
              railIndex={3}
              label="Time amount"
              value={fmtMoney(totals?.amount_cents ?? 0, currency)}
              sub="Hourly projects"
              delta={timeDeltas?.delta}
              deltaDir={timeDeltas?.deltaDir}
            />
            <ReportsKpiCard
              railIndex={4}
              label="Grand total"
              value={fmtMoney(grandTotalCents, currency)}
              sub="Time + flat fees"
              delta={grandDeltas?.delta}
              deltaDir={grandDeltas?.deltaDir}
            />
          </div>

          {/* Section tables ────────────────────────────────────── */}
          <div
            className={cn(
              'relative z-[1] grid gap-4',
              layout === 'split'
                ? 'grid-cols-1 [@media(min-width:1180px)]:grid-cols-[1.6fr_1fr]'
                : 'grid-cols-1',
            )}
          >
            {/* By project */}
            <ReportsSection
              title="By project"
              count={byProject.length}
              glyph="circle"
              glyphColor="oklch(0.65 0.13 245)"
              meta={[
                { key: 'total', value: fmtHours(totals?.minutes ?? 0) },
                { key: 'flat', value: fmtMoney(totalFlatCents, currency) },
              ]}
            >
              {stackSegments.length > 0 && (totals?.minutes ?? 0) > 0 ? (
                <div className="px-[18px] pt-3.5 pb-1">
                  <StackBar segments={stackSegments} />
                </div>
              ) : null}

              <ReportsProjectHeader showSpark={(data?.day_count ?? 0) > 0} />

              {byProject.length === 0 ? (
                <EmptyRow
                  title="No projects with activity in this range."
                  sub="Try widening the range, or check that contributors have logged time recently."
                />
              ) : (
                byProject.map((p) => {
                  const flat =
                    p.billing_type === 'flat' && (p.minutes ?? 0) > 0
                      ? p.flat_fee_cents ?? 0
                      : 0;
                  const total = (p.amount_cents ?? 0) + flat;
                  return (
                    <ReportsProjectRow
                      key={p.project_id ?? p.project_slug ?? p.name}
                      projectSlug={p.project_slug ?? ''}
                      name={p.name ?? '—'}
                      billingType={p.billing_type ?? 'none'}
                      dailyMinutes={p.daily_minutes ?? []}
                      weekendIndices={weekendIndices}
                      totalMinutes={p.minutes ?? 0}
                      timeAmountCents={p.amount_cents ?? 0}
                      totalAmountCents={total}
                      currency={currency}
                    />
                  );
                })
              )}
            </ReportsSection>

            {/* By user */}
            <ReportsSection
              title="By teammate"
              count={byUser.length}
              glyph="square"
              glyphColor="var(--brand-accent)"
              meta={[
                {
                  key: 'avg billable',
                  value: byUser.length === 0 ? '—' : `${avgBillablePct}%`,
                },
              ]}
            >
              <ReportsUserHeader />
              {byUser.length === 0 ? (
                <EmptyRow
                  title="No users with logged time."
                  sub="Once teammates log time on projects in this range, they'll show up here."
                />
              ) : (
                byUser.map((u) => (
                  <ReportsUserRow
                    key={u.user_id ?? u.email}
                    userId={u.user_id ?? ''}
                    name={u.name ?? null}
                    email={u.email ?? null}
                    totalMinutes={u.minutes ?? 0}
                    billableMinutes={u.billable_minutes ?? 0}
                    amountCents={u.amount_cents ?? 0}
                    currency={currency}
                  />
                ))
              )}
            </ReportsSection>
          </div>
        </>
      )}
    </div>
  );
};

const EmptyRow = ({ title, sub }: { title: string; sub: string }) => (
  <div className="flex flex-col items-center justify-center gap-2.5 px-5 py-12 text-center">
    <span
      className={cn(
        'inline-flex size-11 items-center justify-center rounded-xl border',
        'border-border bg-secondary text-foreground-faint',
      )}
    >
      <BarChart3 className="size-5" aria-hidden="true" />
    </span>
    <p className="text-foreground m-0 text-[13.5px] font-medium">{title}</p>
    <p className="text-foreground-faint m-0 max-w-[380px] text-[12px]">{sub}</p>
  </div>
);
